import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import 'react-owl-carousel2/lib/styles.css';

// UTILITY
import { analyticsGetUserId } from '@/utility/analytics';

// STORE
import { AuthActions } from '@/store/actions';

// HOC COMPONENTS
import CacheBuster from '@/hoc/CacheBuster';

// COMPONENTS
import SnackbarProvider from '@/components/SnackbarProvider';

// THEME
import theme from '@/theme/materialTheme';

// ROUTES
import Routes from '@/routes';

// STYLES
import GlobalStyle from '@/styles';

const App = props => {
  const { partner, anonymousId, setAnonymousId } = props;
  const partnerSlug = partner.slug || partner.partner_slug || '';

  useEffect(() => {
    if (!anonymousId) {
      window.analytics.ready(() => {
        const segmentId = analyticsGetUserId();

        setAnonymousId(segmentId);
      });
    }
  }, [anonymousId, setAnonymousId]);

  return (
    <CacheBuster>
      {({ loading, isLatestVersion, refreshCacheAndReload }) => {
        if (loading) return null;
        if (!loading && !isLatestVersion) {
          refreshCacheAndReload();
        }

        return (
          <div className="App">
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <MuiThemeProvider theme={theme}>
                <SnackbarProvider>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Routes />
                    <GlobalStyle
                      partner={partnerSlug}
                      theme={theme}
                      extraStyles={partner.extra_styles}
                    />
                  </LocalizationProvider>
                </SnackbarProvider>
              </MuiThemeProvider>
            </ThemeProvider>
          </div>
        );
      }}
    </CacheBuster>
  );
};

App.propTypes = {
  partner: PropTypes.object.isRequired,
  setAnonymousId: PropTypes.func.isRequired,
  anonymousId: PropTypes.string.isRequired,
};

const mapStateToProps = ({ auth }) => ({
  partner: auth.partner,
  anonymousId: auth.anonymousId,
});

const mapDispatchToProps = dispatch => ({
  setAnonymousId: id => dispatch(AuthActions.setAnonymousId.action({ id })),
});

const AppWithRedux = connect(mapStateToProps, mapDispatchToProps)(App);

export default AppWithRedux;
